import { ShieldCheck, CreditCard, Lock, MessageCircle, QrCode } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { inr, split4060, waLink } from "@/api";
import { useLang, tr } from "@/i18n";

const steps = [
    "Select your ritual package and submit the booking form.",
    "Pay the 40% advance securely online via Razorpay.",
    "Booking is confirmed instantly — payment ID is saved to your booking.",
    "Pay the remaining 60% one day before arrival for a smooth process.",
];

const methods = ["UPI", "Cards", "NetBanking", "Wallets"];

export const Payment = ({ settings, pkg }) => {
    const { lang } = useLang();
    const t = (s) => tr(lang, s);
    const split = pkg ? split4060(pkg.price) : null;
    const qrSrc = settings?.qr_data || "/upi-qr.jpg";
    return (
        <section id="payment" data-testid="payment-section" className="py-20 lg:py-28 bg-[#241D19] relative grain-overlay">
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                <Reveal>
                    <Overline light>{t("Chapter 03 — Secure Payment")}</Overline>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl text-amber-50 tracking-tight">
                        {t("Pay 40% Advance Online — Instant Confirmation")}
                    </h2>
                    <p className="mt-4 max-w-2xl text-stone-400 leading-relaxed">
                        {t("Book your ritual and pay the advance securely via Razorpay — UPI, cards and netbanking accepted. Booking is confirmed instantly after payment; no screenshots or reference numbers needed.")}
                    </p>
                </Reveal>

                {pkg && split && (
                    <Reveal delay={0.05}>
                        <div data-testid="payment-selected-package" className="mt-8 inline-flex flex-wrap items-center gap-x-6 gap-y-2 bg-[#1C1613] border border-[#D4AF37]/40 rounded-2xl px-6 py-4">
                            <span className="text-xs uppercase tracking-[0.2em] text-amber-300">{t("Selected Package")}</span>
                            <span className="font-serif-alt text-lg text-amber-50">{t(pkg.name)}</span>
                            <span className="text-sm text-stone-400">
                                {t("Total")} <b className="text-amber-50">{inr(pkg.price)}</b> · {t("Advance")} <b className="text-amber-300">{inr(split.advance)}</b> · {t("Balance")} <b className="text-amber-50">{inr(split.balance)}</b>
                            </span>
                        </div>
                    </Reveal>
                )}

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <Reveal delay={0.1} className="h-full">
                        <div data-testid="payment-razorpay-card" className="relative bg-[#FAF6F0] rounded-2xl p-8 flex flex-col items-center text-center h-full ring-2 ring-[#C85A17]">
                            <span className="absolute -top-3 px-3 py-1 rounded-full bg-[#C85A17] text-white text-[10px] font-semibold uppercase tracking-widest">
                                {t("Recommended")}
                            </span>
                            <div className="w-14 h-14 rounded-2xl bg-[#241D19] flex items-center justify-center mb-5">
                                <Lock size={24} className="text-amber-300" />
                            </div>
                            <h3 className="font-display text-xl text-[#241D19]">{t("Secure Payment by Razorpay")}</h3>
                            <p className="mt-3 text-sm text-[#6E6259] leading-relaxed">
                                {t("Your advance goes directly to Dharm Sarovar Private Limited's verified account.")}
                            </p>
                            <div className="mt-5 flex flex-wrap justify-center gap-2">
                                {methods.map((m) => (
                                    <span key={m} className="px-3.5 py-1.5 rounded-full bg-white border border-[#E6DBCF] text-xs font-medium text-[#241D19]">
                                        {m}
                                    </span>
                                ))}
                            </div>
                            <a
                                href="#book"
                                data-testid="payment-book-pay-button"
                                className="mt-7 w-full py-3.5 rounded-full bg-[#C85A17] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#9C370B] transition-colors duration-300"
                            >
                                <CreditCard size={18} /> {t("Book & Pay Advance")}
                            </a>
                            <p className="mt-4 text-[11px] text-[#B0A494]">256-bit SSL · PCI-DSS · Razorpay</p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2} className="h-full">
                        <div data-testid="payment-qr-card" className="bg-[#FAF6F0] rounded-2xl p-8 flex flex-col items-center text-center h-full">
                            <div className="flex items-center gap-2 text-[#9C370B] mb-5">
                                <QrCode size={20} />
                                <span className="text-xs uppercase tracking-[0.25em] font-semibold">{t("Pay via UPI QR")}</span>
                            </div>
                            <img
                                src={qrSrc}
                                alt="Dharm Sarovar UPI QR Code"
                                data-testid="payment-upi-qr-image"
                                className="w-48 rounded-xl border border-[#E6DBCF] shadow-lg"
                            />
                            <p className="mt-4 font-display text-xs tracking-wide text-[#241D19]">DHARM SAROVAR PRIVATE LIMITED</p>
                            <p className="mt-1 text-[11px] text-[#6E6259]">GPay · PhonePe · Paytm · BHIM</p>
                            <p className="mt-3 text-xs text-[#6E6259] leading-relaxed">
                                {t("After QR payment, submit your booking and share the payment details on WhatsApp.")}
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.3} className="h-full">
                        <div
                            data-testid="payment-how-it-works-card"
                            className="bg-[#1C1613] border border-[#342A24] rounded-2xl p-8 h-full flex flex-col"
                        >
                            <div className="flex items-center gap-2 text-amber-300 mb-6">
                                <ShieldCheck size={20} />
                                <span className="text-xs uppercase tracking-[0.25em] font-semibold">
                                    {t("How Payment Works")}
                                </span>
                            </div>
                            <ol className="space-y-4 flex-1">
                                {steps.map((s, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-stone-300 leading-relaxed">
                                        <span className="font-display text-amber-300 text-xs mt-1">{String(i + 1).padStart(2, "0")}</span>
                                        {t(s)}
                                    </li>
                                ))}
                            </ol>
                            <div className="mt-6 flex items-start gap-2.5 bg-[#241D19] border border-[#D4AF37]/40 rounded-xl p-4">
                                <ShieldCheck size={18} className="text-amber-300 shrink-0 mt-0.5" />
                                <p data-testid="payment-verification-note" className="text-xs text-amber-100/90 leading-relaxed">
                                    {t("Payments processed securely by Razorpay. Booking is confirmed instantly after successful payment.")}
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.35}>
                    <p data-testid="refund-policy-note" className="mt-10 max-w-3xl mx-auto text-center text-xs text-stone-500 leading-relaxed border border-[#342A24] rounded-xl px-6 py-4">
                        <span className="text-amber-300 font-semibold">{t("Cancellation & Refund Policy")}: </span>
                        {t("Full advance refund for cancellations made at least 72 hours before the ritual date; 50% refund within 72 hours. Refunds are processed to the original payment method within 5–7 working days.")}
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
